import React, { useState, useContext, useMemo, useEffect } from 'react';
import Router, { useRouter } from 'next/router';
import Fetch from '@/utils/Fetch';
import SettingsMenu from '@/components/settings/menu';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import ToastContext from '@/utils/toast-context';
import ConfirmAction from '@/components/modals/confirm-action';
import Time from '@/utils/time';
import BillingService from '@/services/billing-service';
import SubscriptionContext from '@/utils/subscription-context';
import SCMessageBarContext from '@/utils/contexts/sc-message-bar-context';
import CancelSubscription from "@/PageComponents/Settings/Subscriptions/CancelSubscription";
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import ScNumberControl from '@/components/sc-controls/form-controls/v2/sc-number-control';
import MessageBarHelper from '@/utils/message-bar-helper';
import constants from '@/utils/constants';
import SCTextArea from '@/components/sc-controls/form-controls/sc-textarea';
import SCCheckbox from '@/components/sc-controls/form-controls/sc-checkbox';
import {Tabs as MantineTabs, Button as MantineButton, Flex, Text, Box, ActionIcon, Badge, Title, Grid, Tooltip, Divider, Table, Card} from '@mantine/core';
import tabStyles from '@/styles/Tabs.module.css'
import styles from '@/pages/settings/subscription/manage.module.css';
import useInitialTimeout from '@/hooks/useInitialTimeout';
import usePopupClosed from '@/hooks/usePopupClosed';
import BillingContactTable from '@/PageComponents/Table/Component Tables/BillingContactTable';
import storage from '@/utils/storage';
import SimpleTable from '@/PageComponents/SimpleTable/SimpleTable';
import moment from "moment";
import { IconRefresh, IconBulb } from '@tabler/icons-react';
import UsageSummary, {SubscriptionInfoLike, UsageStats} from '@/PageComponents/Subscription/UsageSummary';
import {useQuery} from "@tanstack/react-query";
import ScPagination from '@/PageComponents/Table/ScPagination';
import { ManagerTenantBillingDetails, ManagerTenantBillingContactDetails } from '@/interfaces/api/models';
import DownloadService from '@/utils/download-service';
import PS from '@/services/permission/permission-service';
import { createFileRoute } from '@tanstack/react-router'

interface SubscriptionManageProps {}

interface CheckoutInputs {
  voucher_code?: string;
  [key: string]: any;
}

interface CheckoutErrors {
  voucher_code?: string;
  [key: string]: any;
}

interface BillingDetailsErrors {
  RegisteredName?: string;
  CompanyRegistrationNumber?: string;
  VATRegistrationNumber?: string;
  PhysicalAddress?: string;
  PostalAddress?: string;
  Name?: string;
  EmailAddress?: string;
  MobileNumber?: string;
  [key: string]: any;
}

interface SubscriptionInfo {
  BillingAccount?: any;
  CustomerStatusEnum?: number;
  IsThirdParty?: boolean;
  UserCount?: number;
  Rate?: number;
  VoucherID?: string;
  VoucherCode?: string;
  GrossFeeToSubscribe?: number;
  UserRate?: number;
  StoreRate?: number;
  UserRateDiscounted?: number;
  StoreRateDiscounted?: number;
  UserDiscountPercentage?: number;
  StoreDiscountPercentage?: number;
  UserPromotionValidityMonths?: number;
  StorePromotionValidityMonths?: number;
  UserDiscountEnd?: string;
  StoreDiscountEnd?: string;
  ProrataBillingRatio?: number;
  OwingBalance?: number;
  StoreCount?: number;
  LicenceFeeNextInclVAT?: number;
  AccessStatus?: number;
  PaymentErrorMessage?: string;
  SMSRateExVAT?: number;
  VATPercentage?: number;
  SMSCreditsPurchased?: number;
  SMSCreditsUsed?: number;
  SMSRateExVATList?: Array<{ LowCount: number; HighCount?: number; RateExclVAT: number }>;
  [key: string]: any;
}

interface MessageCredit {
  PurchaseDate: string;
  Quantity: number;
  Reference: string;
  MessageType: number;
}

interface Invoice {
  ID: string;
  InvoiceNumber: string;
  TotalInclusive: number;
  InvoiceDate: string;
  Paid: boolean;
  PaidFully: boolean;
  Reference: string;
  CustomReference?: string;
  AmountDue: number;
}

function SubscriptionManage(props: SubscriptionManageProps) {
  const router = useRouter();
  const { tab } = router.query as any;
 // TODO(tanstack-router): verify router.query → useParams/useSearch
  const toast: any = useContext(ToastContext);
  const watchPopup = usePopupClosed();
  const subscriptionContext = useContext(SubscriptionContext);
  const messageBarContext = useContext(SCMessageBarContext);

  let currPath = '';
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const minCredits = 1000;

  const [thirdPartyBilling, setThirdPartyBilling] = useState<boolean>(false);
  const [canCreateBilling, setCanCreateBilling] = useState<boolean>(false);
  const [subscriptionCancelled, setSubscriptionCancelled] = useState<boolean>(false);
  const [checkoutInputs, setCheckoutInputs] = useState<CheckoutInputs>({});
  const [checkoutErrors, setCheckoutErrors] = useState<CheckoutErrors>({});
  const [confirmOptions, setConfirmOptions] = useState<any>(Helper.initialiseConfirmOptions());
  const [selectedTab, setSelectedTab] = useState<string>('subscription');
  // const [smsEnabled, setSmsEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (router.isReady) {
      setSelectedTab(tab === 'sms' ? 'sms' : 'subscription');
    }
  }, [router.isReady]);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({});
  const [messageCredits, setMessageCredits] = useState<MessageCredit[]>([]);
  const [creditCount, setCreditCount] = useState<number | undefined>(undefined);
  const [creditError, setCreditError] = useState<string>("");
  const [billingDetails, setBillingDetails] = useState<ManagerTenantBillingDetails | undefined>();
  const [billingDetailsErrors, setBillingDetailsErrors] = useState<BillingDetailsErrors>({});
  const [postalSameAsPhysical, setPostalSameAsPhysical] = useState<boolean>(false);
  const [billingContactMobileMissing, setBillingContactMobileMissing] = useState<boolean>(false);
  const [noVATNumber, setNoVATNumber] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [initialising, setInitialising] = useState<boolean>(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicePageIndex, setInvoicePageIndex] = useState<number>(0);
  const [invoiceTotalResults, setInvoiceTotalResults] = useState<number>(0);
  const invoicePageSize = 10;

  const usageStatsQuery = useQuery<UsageStats>({
    queryKey: ['usage-stats'],
    queryFn: () => BillingService.getUsage(),
  });

  const getBillingDetails= async (initial: boolean = false) => {
    let details = await BillingService.getTenantBillingDetails();
    setBillingDetails(details);
    setPostalSameAsPhysical(details?.PostalAddress === details?.PhysicalAddress);
    let primaryContact = details?.Contacts?.find((x: any) => x.IsActive && x.IsPrimaryAccount);
    setBillingContactMobileMissing(Helper.isNullOrWhitespace(primaryContact?.MobileNumber));
    if (initial) {
      if (!details?.VATRegistrationNumber) {
        setNoVATNumber(true);
      }
    }
  }

  const getInvoices = async (pageIndex: number = 0) => {
    const response = await Fetch.get({
      url: '/Billing/Invoices',
      params: {
        PageIndex: pageIndex,
        PageSize: invoicePageSize,
        SortExpression: 'InvoiceDate',
        SortDirection: 'desc'
      }
    });

    if (response.HttpStatusCode === 200 && response.Results) {
      setInvoices(response.Results);
      setInvoiceTotalResults(response.TotalResults || 0);
      setInvoicePageIndex(pageIndex);
    }
  }

  const downloadInvoice = (invoiceID: string, invoiceNumber: string) => {
    DownloadService.downloadFile('GET', `/Billing/InvoicePDF?invoiceID=${invoiceID}`, null, false, false);
  }

  const previewInvoice = (invoiceID: string) => {
    DownloadService.downloadFile('GET', `/Billing/InvoicePDF?invoiceID=${invoiceID}`, null, true, false);
  }

  const renderStatusPill = () => {
    const status = subscriptionInfo.CustomerStatusEnum;
    const accessStatus = subscriptionInfo.AccessStatus;

    let label = '';
    let color = 'gray';

    if (status === Enums.CustomerStatus.Trial) {
      label = 'Trial';
      color = 'scBlue';
    } else if (status === Enums.CustomerStatus.Cancelled) {
      label = 'Cancelled';
      color = 'red.7';
    } else if (status === Enums.CustomerStatus.Live) {
      if (accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
        label = 'Paused';
        color = 'yellow.5';
      } else {
        label = 'Active';
        color = 'green.6';
      }
    }

    if (!label) return null;

    return (
      <Flex align="center" gap={8}>
        <Text size="sm" c="gray.6">Subscription</Text>
        <Badge
          radius="xl"
          variant={status === Enums.CustomerStatus.Cancelled || status === Enums.CustomerStatus.Trial ? 'light' : "filled"}
          color={color}
          autoContrast={status === Enums.CustomerStatus.Trial}
          size="sm"
          tt="none"
          fw={700}
        >
          {label}
        </Badge>
      </Flex>
    );
  };

  const [editedReferences, setEditedReferences] = useState<{ [invoiceId: string]: string }>({});
  const [savingReference, setSavingReference] = useState<string | null>(null);

  const handleReferenceChange = (invoiceId: string, value: string) => {
    setEditedReferences((prev: any) => ({
      ...prev,
      [invoiceId]: value
    }));
  };

  const hasReferenceChanged = (invoice: Invoice): boolean => {
    const editedValue = editedReferences[invoice.ID];
    if (editedValue === undefined) return false;
    return editedValue !== (invoice.CustomReference || '');
  };

  const saveCustomReference = async (invoiceId: string) => {
    const newReference = editedReferences[invoiceId];
    if (newReference === undefined) return;

    setSavingReference(invoiceId);

    try {
      const response = await Fetch.put({
        url: `/Billing/InvoiceCustomReference`,
        params: {
          InvoiceID: invoiceId,
          CustomReference: newReference
        }
      });

      // Check for success - Ok() returns 200 or no error message
      const isSuccess = response?.HttpStatusCode === 200 || (response?.HttpStatusCode === undefined && !response?.Message);

      if (isSuccess) {
        // Update local state
        setInvoices((prev: any) => prev.map((inv: any) =>
          inv.ID === invoiceId ? { ...inv, CustomReference: newReference } : inv
        ));
        // Clear the edited state for this invoice
        setEditedReferences((prev: any) => {
          const updated = { ...prev };
          delete updated[invoiceId];
          return updated;
        });
        toast.setToast({
          message: "Custom reference updated successfully",
          show: true,
          type: Enums.ToastType.success
        });
      } else {
        toast.setToast({
          message: response?.Message || "Failed to update custom reference",
          show: true,
          type: Enums.ToastType.error
        });
      }
    } catch (error) {
      toast.setToast({
        message: "Failed to update custom reference",
        show: true,
        type: Enums.ToastType.error
      });
    } finally {
      setSavingReference(null);
    }
  };

  useInitialTimeout(0, async () => {

    setInitialising(true);
    const [a, b, c, d] = await Promise.all([getBillingAccount(), getMessageCredits(), getBillingDetails(true), getInvoices()]);
    setInitialising(false);
  });

  const getBillingAccount = async () => {

    let count = 0;
    let rate = 0;
    let billingAccount = null;

    const [subscription, message] = await BillingService.getSubcriptionInfo(subscriptionContext, true);

    if (subscription) {
      // all good
      billingAccount = subscription.BillingAccount?.IsActive === false ? null : subscription.BillingAccount;
      let cancelled = subscription.CustomerStatusEnum === Enums.CustomerStatus.Cancelled;
      let trial = subscription.CustomerStatusEnum === Enums.CustomerStatus.Trial;
      let signup = subscription.CustomerStatusEnum === Enums.CustomerStatus.SignUp;
      setThirdPartyBilling(subscription.IsThirdParty);
      let createCustomerStatus = cancelled || trial || signup;
      setCanCreateBilling(createCustomerStatus);
      if (createCustomerStatus) {
        setNoVATNumber(false);
      }
      setSubscriptionInfo(subscription);
      setSubscriptionCancelled(cancelled);
      count = subscription.UserCount;
      rate = subscription.Rate;
      getVoucherStatus(subscription);
      MessageBarHelper.updateMessageBar(subscription, messageBarContext);
    } else {
      setCanCreateBilling(false);
      setThirdPartyBilling(false);
      setSubscriptionInfo({});
      setSubscriptionCancelled(false);
      toast.setToast({
        message: message,
        show: true,
        type: Enums.ToastType.success
      });
    }

    return [count, rate, billingAccount];
  };

  const getMessageCredits = async () => {

    const credits = await Fetch.get({
      url: '/MessageCredit'
    });

    if (credits.HttpStatusCode === 200) {
      setMessageCredits(credits.Results);
    }
  };

  const updateCheckoutInputs = (value: any, field: string) => {
    setCheckoutInputs({
      ...checkoutInputs,
      [field]: value
    });
  };

  const masterAdminBlocked = (): boolean => {
    if (PS.isMasterSystemAdmin()) {
      toast.setToast({
        message: "This function is disabled for ServCraft admins.",
        show: true,
        type: Enums.ToastType.error
      });
      return true;
    }
    return false;
  };

  const submitDebitOrderRequest = async (forceNewPending: boolean) => {
    if (PS.isMasterSystemAdmin()) return;

    let doSaveBillingDetails = canCreateBilling;

    if (doSaveBillingDetails) {
      if (!await saveBillingDetails(true)) {
        return;
      }
    }

    let win = window.open(`/debit-order?t=${storage.getCookie(Enums.Cookie.tenantID)}&f=${forceNewPending}`, "_blank");

    const handlePopupMessage = (event: MessageEvent) => {
      if (!event || !event.data) return;
      switch (event.data.scType) {
        case "close":
          win?.close();
          window.removeEventListener("message", handlePopupMessage);
          break;
        default:
          break;
      }
    }

    window.addEventListener("message", handlePopupMessage);

    watchPopup(win, () => {
      getBillingAccount();
      getMessageCredits();
      getBillingDetails();
    });
  };

  const doPrepForCardCheckout = async (isSettlement: boolean, isResubscription: boolean) => {

    if (!Helper.isNullOrWhitespace(checkoutInputs.voucher_code) && voucherStatus !== Enums.VoucherStatus.Applied) {
      toast.setToast({
        message: "Please apply a valid voucher code before proceeding.",
        show: true,
        type: Enums.ToastType.error
      });
      setCheckoutErrors({
        ...checkoutErrors,
        voucher_code: "Invalid voucher code"
      });
      return { isValid: false, firstName: null, lastName: null, emailAddress: null };
    }

    let doSaveBillingDetails = !isSettlement && !isResubscription;

    let contact = getPrimaryContact();
    let nameSplit = contact?.Name?.split(" ") ?? [];

    let firstName = nameSplit.length > 0 ? nameSplit.splice(0, 1)[0] : null;
    let lastName = nameSplit.length > 0 ? nameSplit.join(" ") : null;
    let emailAddress = contact?.EmailAddress;

    if (doSaveBillingDetails) {
      if (!await saveBillingDetails(false)) {
        return { isValid: false, firstName, lastName, emailAddress };
      }
    }

    let isValid = true;

    // check if billing audits have changed regardless of validation
    let [oldCount, oldRate] = [subscriptionInfo.UserCount, subscriptionInfo.Rate];
    let [newCount, newRate] = await getBillingAccount();

    if (oldCount !== newCount || oldRate !== newRate) {
      isValid = false;
      toast.setToast({
        message: "Warning: Billing total has changed, please review before confirming again",
        show: true
      });
    }

    return { isValid, firstName, lastName, emailAddress };

  }

  const goToPeachPaymentPortalUrl = async (isSettlement?: boolean, isResubscription?: boolean) => {

    let { isValid, firstName, lastName, emailAddress } = await doPrepForCardCheckout(isSettlement ?? false, isResubscription ?? false);

    if (isValid || isSettlement == true || isResubscription == true) {

      if (isSettlement) {
        Helper.mixpanelTrack(constants.mixPanelEvents.settleSubscription);
      } else {
        Helper.mixpanelTrack(constants.mixPanelEvents.createSubscription);
      }

      let win = window.open(`/settings/subscription/peach?origin=${encodeURIComponent(window.location.origin)}&isSettlement=${isSettlement === true}&isResubscription=${isResubscription === true}&firstName=${encodeURIComponent(firstName ?? '')}&lastName=${encodeURIComponent(lastName ?? '')}&emailAddress=${encodeURIComponent(emailAddress ?? '')}`, "_blank");

      watchPopup(win, () => {
        getBillingAccount();
        getMessageCredits();
      });
    }
  }

  const submitPayFastCheckout = async (isSettlement: boolean = false, isResubscription: boolean = false, forcePayNow: boolean = false) => {

    let { isValid, firstName, lastName, emailAddress } = await doPrepForCardCheckout(isSettlement, isResubscription);

    if (isValid || isSettlement == true || isResubscription == true) {

      //preparing submission object
      let params = {
        name_first: firstName,
        name_last: lastName,
        email_address: emailAddress,
        isSettlement: isSettlement,
        isResubscription: isResubscription,
        forcePayNow: forcePayNow
      };

      setSubmitting((_: any) => true);

      const redirUrl = await Fetch.post({
        url: '/Billing/PayFastCheckoutUrlAdhoc',
        params
      });

      setSubmitting((_: any) => false);

      if (isSettlement) {
        Helper.mixpanelTrack(constants.mixPanelEvents.settleSubscription);
      } else {
        Helper.mixpanelTrack(constants.mixPanelEvents.createSubscription);
      }

      let win = window.open(redirUrl, "_blank");
      watchPopup(win, () => {
        getBillingAccount();
        getMessageCredits();
      });
    }
  };

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  const refreshBillingAccount = async () => {
    await getBillingAccount();
  }

  const cancelSubscription = async () => {
    setShowCancelModal(true);
  };

  const buyCreditsPrompt = () => {
    let isValid = true;
    let cc = parseInt((creditCount ?? 0).toString());
    if (isNaN(cc) || cc < minCredits) {
      isValid = false;
      setCreditError(`Minimum amount of ${minCredits} credits must be purchased`)
    }

    if (!isValid) return;

    let smsRateInclVAT = (subscriptionInfo.SMSRateExVAT ?? 0) * (1 + (subscriptionInfo.VATPercentage ?? 0) / 100);
    let total = smsRateInclVAT * (creditCount ?? 0);

    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      display: true,
      confirmButtonText: "Confirm",
      heading: "Confirm buy more sms credits?",
      text: `Your total amount will be R ${total.toFixed(2)} for ${creditCount} SMS credits.
      You will be redirected to PayFast to make a once off payment.`,
      onConfirm: async () => {
        await buyCredits();
      }
    });
  };

  const buyCredits = async () => {
    var params = {
      creditCount: creditCount
    };
    const redirUrl = await Fetch.post({
      url: '/Billing/PayFastCheckoutUrlOnceOffSMSCredits',
      params
    });

    Helper.mixpanelTrack(constants.mixPanelEvents.buyCredits);

    let win = window.open(redirUrl, "_blank");
    watchPopup(win, () => {
      getBillingAccount();
      getMessageCredits();
    });
  };

  const gotoEmployees = () => {
    Helper.nextRouter(Router.push, "/settings/employee/list", "/settings/employee/list");
  };

  const getNextMonth = () => {
    var date = Time.now();
    date.setMonth(date.getMonth() + 1);
    var month = (date.getMonth() + 1).toString();
    if (month.length === 1) {
      month = `0${month}`;
    }
    var year = date.getFullYear().toString();
    return Time.parseDate(`${year}-${month}-01 00:00:00`);
  };

  // Vouchers

  const [voucherStatus, setVoucherStatus] = useState<number>(Enums.VoucherStatus.None);

  const applyVoucher = async () => {
    let voucherCode = checkoutInputs.voucher_code;

    if (Helper.isNullOrWhitespace(voucherCode)) {
      //   message: 'Voucher code required',
      //   show: true,
      //   type: Enums.ToastType.error
      setCheckoutErrors({
        ...checkoutErrors,
        voucher_code: undefined
      });
      return;
    }

    setSubmitting(true);

    let request = await BillingService.applyVoucher(voucherCode, toast);

    if (request && request.status === 500) {
      // do nothing, something went wrong
      // toast will automatically popup
      setCheckoutErrors({
        ...checkoutErrors,
        voucher_code: "Invalid voucher code"
      });
    } else {
      toast.setToast({
        message: 'Voucher applied successfully',
        show: true,
        type: Enums.ToastType.success
      });
      getBillingAccount();
    }

    setSubmitting(false);
  };

  const getVoucherStatus = (subscription: SubscriptionInfo) => {
    let voucherID = subscription.VoucherID;
    let voucherCode = subscription.VoucherCode;

    let status = Enums.VoucherStatus.None;

    if (!Helper.isNullOrWhitespace(voucherID)) {
      status = Enums.VoucherStatus.Applied;
    } else if (!Helper.isNullOrUndefined(voucherCode)) {
      status = Enums.VoucherStatus.Invalid;
    }

    setCheckoutInputs({
      ...checkoutInputs,
      voucher_code: voucherCode
    });

    setVoucherStatus(status);
  };

  const displayThirdPartyBilling = () => {
    return (
      <Box>
        <Title order={3} mb="xs">Subscription Details</Title>

        {canCreateBilling && <Text mb="sm">You are still in your trial period</Text>}

        <Text mb="sm">Your billing details are handled by a third party. Please enquire directly with your third party for any billing enquiries.</Text>

        <Text mb="sm">Alternatively, contact ServCraft support.</Text>
      </Box>
    );
  };

  const getPrimaryContact = (): ManagerTenantBillingContactDetails | undefined => {
    if (!billingDetails) return undefined;
    let primaryContact = billingDetails.Contacts.find((x: any) => x.IsActive && x.IsPrimaryAccount);
    return primaryContact;
  }

  const updatePrimaryContact = (e: any) => {
    let contacts = [...(billingDetails?.Contacts ?? [])];
    let contact = contacts.find((x: any) => x.IsActive && x.IsPrimaryAccount);
    if (contact) {
      contact[e.name] = e.value;
      updateBillingDetails({ name: "Contacts", value: contacts });
    }
  }

  const validateBillingDetails = (validateContact: boolean): boolean => {

    let contact = getPrimaryContact();

    let inps: any[] = [];

    if (validateContact) {
      inps.push(...[
        {
          required: true,
          key: "Name",
          value: contact?.Name,
          type: Enums.ControlType.Text
        }, {
          required: true,
          key: "EmailAddress",
          value: contact?.EmailAddress,
          type: Enums.ControlType.Email
        }, {
          required: true,
          key: "MobileNumber",
          value: contact?.MobileNumber,
          type: Enums.ControlType.ContactNumber
        }
      ]);
    }

    inps.push(...[{
      required: true,
      key: "RegisteredName",
      value: billingDetails?.RegisteredName,
      type: Enums.ControlType.Text
    }, {
      required: true,
      key: "PhysicalAddress",
      value: billingDetails?.PhysicalAddress,
      type: Enums.ControlType.Text
    }]);

    if (!noVATNumber) {
      inps.push({
        required: true,
        key: "VATRegistrationNumber",
        value: billingDetails?.VATRegistrationNumber,
        type: Enums.ControlType.Text
      });
    }

    let { isValid, errors } = Helper.validateInputs(inps);

    if (validateContact && !(errors as any).Name && contact) {
      let split = contact.Name?.split(" ") ?? [];
      if (split.length < 2) {
        isValid = false;
        (errors as any).Name = "Must contain first and last names";
      }
    }

    // Validate address line character limit (100 chars per line)
    const physicalLines = (billingDetails?.PhysicalAddress ?? "").split("\n");
    if (physicalLines.some((line: any) => line.length > 100)) {
      isValid = false;
      (errors as any).PhysicalAddress = "Each address line must be 100 characters or less";
    }

    const postalLines = (billingDetails?.PostalAddress ?? "").split("\n");
    if (postalLines.some((line: any) => line.length > 100)) {
      isValid = false;
      (errors as any).PostalAddress = "Each address line must be 100 characters or less";
    }

    setBillingDetailsErrors(errors);

    return isValid;
  }

  const setBillingDetailsFromContactTable = async (newBillingDetails: ManagerTenantBillingDetails) => {
    setBillingDetails(newBillingDetails);
  };

  const saveBillingDetails = async (validateContact: boolean): Promise<boolean> => {

    if (!validateBillingDetails(validateContact)) return false;
    if (!billingDetails) return false;

    setSubmitting(true);
    let saveResult = false;

    let details = await BillingService.updateTenantBillingDetails(billingDetails, toast);
    if (details) {
      setBillingDetails(details);
      saveResult = true;
      toast.setToast({
        message: "Saved company billing details succesfully",
        show: true,
        type: Enums.ToastType.success
      });
    }
    setSubmitting(false);
    return saveResult;
  };

  const updateBillingDetails = (e: any) => {
    if (!billingDetails) return;
    let bd = { ...billingDetails } as ManagerTenantBillingDetails;

    let val = e.value;
    if (e.name.includes("Address")) {
      const lineLength = 4;
      val = val ?? "";
      let split: string[] = [];
      split = val.split("\n");
      if (split.length > lineLength) {
        split.splice(lineLength, split.length - lineLength);
        val = split.join("\n");
      }
    }

    bd[e.name] = val;

    if (e.name === "PhysicalAddress" && postalSameAsPhysical) {
      bd["PostalAddress"] = val;
    }

    setBillingDetails(bd);
  }

  const displayBillingDetailsFormItems = (showContact: boolean) => {

    let billingRegisteredName = billingDetails?.RegisteredName;
    let billingCompanyRegistrationNumber = billingDetails?.CompanyRegistrationNumber;
    let billingVATRegistrationNumber = billingDetails?.VATRegistrationNumber;
    let billingPhysicalAddress = billingDetails?.PhysicalAddress;
    let billingPostalAddress = billingDetails?.PostalAddress;

    let billingContact = getPrimaryContact();
    let billingContactName = billingContact?.Name;
    let billingContactMobileNumber = billingContact?.MobileNumber;
    let billingContactEmailAddress = billingContact?.EmailAddress;

    return (<Box>

      <Title order={5} mb={'xl'} mt={5}>Billing Details</Title>

      <Text mt="1rem" className={styles.subText}>These are the details that will appear on invoices and statements that we send to you.</Text>

      <Flex gap="md">
        <Box flex={1}>
          <SCInput
            error={billingDetailsErrors.RegisteredName}
            required={true}
            extraClasses="input-width"
            value={billingRegisteredName}
            label="Registered Company Name"
            name="RegisteredName"
            onChange={updateBillingDetails} />
        </Box>
        <Box flex={1}>
          <SCInput
            error={billingDetailsErrors.CompanyRegistrationNumber}
            required={false}
            extraClasses="input-width"
            value={billingCompanyRegistrationNumber}
            label="Reg Number"
            name="CompanyRegistrationNumber"
            onChange={updateBillingDetails} />
        </Box>
      </Flex>
      <Flex gap="md">
        <Box flex={1}>
          <SCInput
            error={billingDetailsErrors.VATRegistrationNumber}
            required={!noVATNumber}
            extraClasses="input-width"
            value={billingVATRegistrationNumber}
            label="VAT #"
            name="VATRegistrationNumber"
            disabled={noVATNumber}
            onChange={updateBillingDetails} />
        </Box>
        <Box flex={1} mt="1.7rem">
          <SCCheckbox
            label="I don't have a VAT number"
            value={noVATNumber as any}
            onChange={(e: boolean) => {
              setNoVATNumber(e);
              if (e === true) {
                updateBillingDetails({ name: "VATRegistrationNumber", value: "" });
              }
            }}
          />
        </Box>
      </Flex>
      <Flex gap="md">
        <Box flex={1}>
          <SCTextArea
            error={billingDetailsErrors.PhysicalAddress}
            required={true}
            extraClasses="input-width"
            value={billingPhysicalAddress}
            label="Physical Address"
            name="PhysicalAddress"
            onChange={updateBillingDetails}
          />
        </Box>
        <Box flex={1}>
          <SCTextArea
            error={billingDetailsErrors.PostalAddress}
            required={false}
            extraClasses="input-width"
            value={billingPostalAddress}
            label="Postal Address"
            name="PostalAddress"
            onChange={updateBillingDetails}
            disabled={postalSameAsPhysical}
          />
          <SCCheckbox
            label='Postal same as physical'
            value={postalSameAsPhysical as any}
            onChange={(val: boolean) => {
              setPostalSameAsPhysical(val);
              if (val) {
                updateBillingDetails({ name: "PostalAddress", value: billingDetails?.PhysicalAddress });
              }
            }}
          />
        </Box>
      </Flex>

      {showContact && <>
        <Title order={5} mb={4} mt={5}>Billing Contact</Title>
        <Text size="sm" c="dimmed" mb={'xl'}>Billing contact can be updated or changed after subscription</Text>

        <Flex gap="md">
          <Box flex={1}>
            <SCInput error={billingDetailsErrors.Name} required={true} extraClasses="input-width" disabled={true}
              value={billingContactName} label="Name" name="Name" onChange={updatePrimaryContact} />
          </Box>
          <Box flex={1}>
            <SCInput error={billingDetailsErrors.EmailAddress} required={true} extraClasses="input-width" disabled={true}
              value={billingContactEmailAddress} label="Email Address" name="EmailAddress" onChange={updatePrimaryContact} />
          </Box>
        </Flex>
        <Flex gap="md">
          <Box flex={1}>
            <SCInput error={billingDetailsErrors.MobileNumber} required={true} extraClasses="input-width" disabled={!billingContactMobileMissing}
              value={billingContactMobileNumber} label="Mobile Number" name="MobileNumber" onChange={updatePrimaryContact} />
          </Box>
          <Box flex={1}></Box>
        </Flex>
      </>}

    </Box>);
  };

  const displayCreateBilling = () => {

    let grossFeeToSubscribe = subscriptionInfo.GrossFeeToSubscribe ? subscriptionInfo.GrossFeeToSubscribe : 0;

    let billingAccount = subscriptionInfo.BillingAccount?.IsActive === false ? null : subscriptionInfo.BillingAccount;
    let isCancelled = billingAccount?.BillingStatus === Enums.BillingStatus.Cancelled || subscriptionCancelled;
    let isSuccess = billingAccount?.BillingStatus === Enums.BillingStatus.Success && !isCancelled;

    let owingBalance = subscriptionInfo.OwingBalance ? subscriptionInfo.OwingBalance : 0;
    let isOwing = owingBalance > 0;

    const accessStatus = subscriptionInfo.AccessStatus;

    let paymentProblem = isOwing &&
        ([Enums.AccessStatus.LiveAndOwing, Enums.AccessStatus.LockedWithAccess, Enums.AccessStatus.LockedWithOutAccess].includes(accessStatus ?? 0));

    return (<Box pt="0.5rem">

      <Flex direction={{base: 'column-reverse', lg: 'row'}} w={'100%'} gap={'sm'} align={'start'}>
        <Flex direction={'column'} miw={200} w={'100%'} maw={640}>
          <Box className={styles.sectionCard}>

            <Flex justify="space-between" align="center" mb="xl">
              <Title order={5} mt={5}>Amount currently due</Title>
              {renderStatusPill()}
            </Flex>

            <Box className={styles.subscriptionBlock}>
              <Text className={styles.subscriptionText} size="2rem"
                    c={paymentProblem ? 'red.7' : undefined}
              >
                <Text component="b">R {grossFeeToSubscribe.toFixed(2)}</Text>
              </Text>
              <Text className={styles.subText}>
                What you pay today (Incl VAT)
              </Text>
            </Box>

            <Divider my="30px" />

            {/*<Box className={styles.voucherBlock}>
              <Text className={styles.subText} fw={500} mb="xs">
                Have a voucher code? Enter it here before you subscribe.
              </Text>
              <Flex gap="md" align="end">
                <Box flex={1}>
                  <SCInput error={checkoutErrors.voucher_code} disabled={submitting || voucherStatus === Enums.VoucherStatus.Applied} onBlur={applyVoucher}
                           value={checkoutInputs.voucher_code} label={"Voucher Code" + (voucherStatus === Enums.VoucherStatus.Applied ? " (Applied)" : "")} onChange={(e: any) => updateCheckoutInputs(e.value, "voucher_code")} />
                </Box>
              </Flex>
            </Box>*/}

            <Box mt="xl">
              {displayBillingDetailsFormItems(true)}
            </Box>
          </Box>
        </Flex>

        <Flex direction={'column'} gap="sm" flex={1}>
          <Box className={styles.sectionCard} miw={200} w={'100%'} maw={640}>
            <UsageSummary
              subscriptionInfo={subscriptionInfo as SubscriptionInfoLike}
              usageStatsQuery={usageStatsQuery}
              isTrial={subscriptionInfo.CustomerStatusEnum === Enums.CustomerStatus.Trial}
              title={subscriptionInfo.CustomerStatusEnum === Enums.CustomerStatus.Trial ? "Subscribe" : "Usage"}
              showSubscriptionOnly={subscriptionInfo.CustomerStatusEnum === Enums.CustomerStatus.Trial}
            />

            <Box mt="xl">
              <Text size="sm" fw={500} mb="md">Recommended</Text>
              <Flex gap="md">
                <Tooltip label="ServCraft Admins cannot activate subscriptions" disabled={!PS.isMasterSystemAdmin()}>
                  <MantineButton
                    onClick={() => submitDebitOrderRequest(false)}
                    disabled={submitting || PS.isMasterSystemAdmin()}
                    flex={1}
                  >
                    Subscribe via debit order
                  </MantineButton>
                </Tooltip>

                <Tooltip label="ServCraft Admins cannot activate subscriptions" disabled={!PS.isMasterSystemAdmin()}>
                  <MantineButton
                    variant="outline"
                    onClick={() => goToPeachPaymentPortalUrl()}
                    disabled={submitting || PS.isMasterSystemAdmin()}
                    flex={1}
                  >
                    Subscribe via card payments
                  </MantineButton>
                </Tooltip>
              </Flex>
            </Box>
          </Box>

          {subscriptionInfo.CustomerStatusEnum === Enums.CustomerStatus.Trial && (
            <Box className={styles.sectionCard} miw={200} w={'100%'} maw={640} mt="sm">
              <UsageSummary
                subscriptionInfo={subscriptionInfo as SubscriptionInfoLike}
                usageStatsQuery={usageStatsQuery}
                isTrial={true}
                showUsageOnly={true}
              />
            </Box>
          )}
        </Flex>
      </Flex>
    </Box>);
  };

  const displayManageBilling = () => {

    let billingAccount = subscriptionInfo.BillingAccount?.IsActive === false ? null : subscriptionInfo.BillingAccount;

    let hasBillingAccount = !!billingAccount;

    let owingBalance = subscriptionInfo.OwingBalance ? subscriptionInfo.OwingBalance : 0;

    let isCancelled = billingAccount?.BillingStatus === Enums.BillingStatus.Cancelled || subscriptionCancelled;
    let isSuccess = billingAccount?.BillingStatus === Enums.BillingStatus.Success && !isCancelled;
    let isBlocked = billingAccount?.BillingStatus === Enums.BillingStatus.Blocked;

    let billingProvider = billingAccount?.BillingProvider ?? Enums.BillingProvider.None;

    let licenceFeeNextExclVAT = usageStatsQuery.data?.EstimatedCurrentBillingAmountExcl ? usageStatsQuery.data?.EstimatedCurrentBillingAmountExcl : 0;
    let accessStatus = subscriptionInfo.AccessStatus;
    let paymentErrorMessage = (billingProvider === Enums.BillingProvider.PayFastOnceOff || billingProvider === Enums.BillingProvider.PeachPayments) ?
      subscriptionInfo.PaymentErrorMessage : "";

    let hasPayfastCardIssue = ((subscriptionInfo?.PaymentErrorMessage ?? "").indexOf("You have entered an invalid card number") > -1
      || (subscriptionInfo?.PaymentErrorMessage ?? "").indexOf("Your card was declined for payment") > -1);

    let isOwing = owingBalance > 0;

    let paymentProblem = isSuccess && isOwing &&
      ([Enums.AccessStatus.LiveAndOwing, Enums.AccessStatus.LockedWithAccess, Enums.AccessStatus.LockedWithOutAccess].includes(accessStatus ?? 0));

    let isPayFast = billingProvider === Enums.BillingProvider.PayFastAdhoc;
    let isDebitOrder = billingProvider === Enums.BillingProvider.DebitOrder;
    let isEFT = billingProvider === Enums.BillingProvider.EFT;
    let isPeach = billingProvider === Enums.BillingProvider.PeachPayments;

    let userRate = (subscriptionInfo.UserRate ?? 0) * (subscriptionInfo.UserCount ?? 0);
    let storeRate = (subscriptionInfo.StoreRate ?? 0) * (subscriptionInfo.StoreCount ?? 0);

    let userRateDiscounted = subscriptionInfo.UserRateDiscounted ?? 0;
    let storeRateDiscounted = subscriptionInfo.StoreRateDiscounted ?? 0;

    let prorataBillingRatio = subscriptionInfo.ProrataBillingRatio ?? 0;
    let prorataRate = ((userRateDiscounted * (subscriptionInfo.UserCount ?? 0)) + (storeRateDiscounted * (subscriptionInfo.StoreCount ?? 0))) * (1 - prorataBillingRatio);

    let userDiscountPercentage = subscriptionInfo.UserDiscountPercentage;
    let storeDiscountPercentage = subscriptionInfo.StoreDiscountPercentage;
    let userDiscountEnd = subscriptionInfo.UserDiscountEnd;
    let storeDiscountEnd = subscriptionInfo.StoreDiscountEnd;

    let userRateDiscount = ((subscriptionInfo.UserRate ?? 0) - userRateDiscounted) * (subscriptionInfo.UserCount ?? 0);
    let storeRateDiscount = ((subscriptionInfo.StoreRate ?? 0) - storeRateDiscounted) * (subscriptionInfo.StoreCount ?? 0);

    let grossFeeToSubscribe = subscriptionInfo.GrossFeeToSubscribe ? subscriptionInfo.GrossFeeToSubscribe : 0;

    interface SubscriptionButton {
      text: string;
      onClick: () => void;
      disabled: boolean;
      emphasise: boolean;
    }

    let subscriptionButtons: SubscriptionButton[] = [];
    let showSettleButton = isOwing;

    const newButton = (text: string = "", onClick: () => void = () => { }, disabled: boolean = false, emphasise: boolean = false): SubscriptionButton => {
      return {
        text,
        onClick,
        disabled,
        emphasise
      };
    }

    switch (billingProvider) {
      case Enums.BillingProvider.PayFastAdhoc:
        if (isOwing) {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Update Card", () => goToPeachPaymentPortalUrl(true, isCancelled), false, true));
          showSettleButton = false;
        } else {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Update Card", () => goToPeachPaymentPortalUrl(false, isCancelled), false, true));
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Debit Order" : "Switch to Debit Order", () => submitDebitOrderRequest(false), false, false));
        }
        break;
      case Enums.BillingProvider.DebitOrder:
        if (isBlocked) {
          if (isOwing) {
            subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Switch to Card", () => goToPeachPaymentPortalUrl(true, isCancelled), false, true));
            showSettleButton = true;
          } else {
            subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Switch to Card", () => goToPeachPaymentPortalUrl(false, isCancelled), false, true));
            subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Debit Order" : "Update Debit Order Details", () => submitDebitOrderRequest(true), false, false));
          }
        }
        else {
          if (isOwing) {
            subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Switch to Card", () => goToPeachPaymentPortalUrl(true, isCancelled), false, true));
            showSettleButton = true;
          } else {
            subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Switch to Card", () => goToPeachPaymentPortalUrl(false, isCancelled), false, true));
            subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Debit Order" : "Update Debit Order Details", () => submitDebitOrderRequest(true), false, false));
          }
        }
        break;
      case Enums.BillingProvider.EFT:
        if (isOwing) {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Switch to Card", () => goToPeachPaymentPortalUrl(true, isCancelled), false, true));
          showSettleButton = true;
        } else {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Switch to Card", () => goToPeachPaymentPortalUrl(false, isCancelled), false, true));
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Debit Order" : "Switch to Debit Order", () => submitDebitOrderRequest(false), false, false));
        }
        break;
      case Enums.BillingProvider.PeachPayments:
        if (isOwing) {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Update Card", () => goToPeachPaymentPortalUrl(true, isCancelled), false, true));
          showSettleButton = true;
        } else {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Update Card", () => goToPeachPaymentPortalUrl(false, isCancelled), false, true));
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Debit Order" : "Switch to Debit Order", () => submitDebitOrderRequest(false), false, false));
        }
        break;
      default:
        if (isOwing) {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Link Card", () => goToPeachPaymentPortalUrl(true, isCancelled), false, true));
          showSettleButton = false;
        } else {
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Card" : "Link Card", () => goToPeachPaymentPortalUrl(false, isCancelled), false, true));
          subscriptionButtons.push(newButton(isCancelled ? "Subscribe with Debit Order" : "Add Debit Order", () => submitDebitOrderRequest(false), false, false));
        }
        break;
    }

    return (
      <Flex direction={{base: 'column-reverse', lg: 'row'}} w={'100%'} gap={'sm'} align={'start'}>
        <Flex direction={'column'} miw={200} w={'100%'} maw={640}>

          <Box className={styles.sectionCard}>

            <Flex justify="space-between" align="center" mb="xl">
              <Title order={5} mt={5}>Amount currently due</Title>
              {renderStatusPill()}
            </Flex>

            <Box className={styles.subscriptionBlock}>
              <Text
                className={styles.subscriptionText}
                size="2rem"
              >
                <b>R {(owingBalance).toFixed(2)}</b>
              </Text>
              <Text className={styles.subText}>
                Payment method: {isPayFast ? "PayFast" : isDebitOrder ? "Debit Order" : isEFT ? "EFT" : isPeach ? "Peach Payments" : "None"}
                {isBlocked === true ? <Text component="span" fw="bold" ml="0.5rem" c="red.7" bd={`1px solid var(--mantine-color-yellow-7)`} p="0.25rem" style={{ borderRadius: "4px", fontSize: "0.8rem" }}> Blocked</Text> : <></>}
              </Text>
            </Box>

            <Divider my="30px" />


            <Box>

              {(isBlocked || !hasBillingAccount) &&
                  <Text mt="sm" mb="lg" size='sm' c="red.7">The subscription payment method must be updated</Text>
              }

              {isPayFast && isOwing && hasPayfastCardIssue && !isCancelled && <>

                <Text size="0.9rem" className={styles.red} component="p">
                  We are currently experiencing an issue with PayFast subscription billing.<br />
                  To ensure uninterrupted service, you can make a payment now by updating your card and settling your account below.
                </Text>
              </>}

              <Box mt="1.5rem">

                <Text fw={"bold"} title="Expand to see other options to settle your account">
                  Recommended Payment Options
                </Text>

                <Flex mt="0.5rem" gap="1rem" wrap="wrap">
                  {subscriptionButtons.map((button: any, index: any) => {
                    return <MantineButton
                        key={index}
                        onClick={() => { if (masterAdminBlocked()) return; button.onClick(); }}
                        disabled={submitting}
                        variant={button.emphasise ? "initial" : "outline"}
                        flex={1}
                        miw="200px"
                    >{button.text}
                    </MantineButton>
                  })}
                </Flex>
              </Box>
            </Box>

            {showSettleButton &&
                <Box mt="1.5rem">
                  <Text fw={"bold"} title="Expand to see other options to settle your account">
                    Alternate Payment Options
                  </Text>

                  <Box>
                    <MantineButton
                        mt="0.5rem"
                        mr="1rem"
                        variant='outline'
                        onClick={() => {
                          if (masterAdminBlocked()) return;
                          if (isBlocked || !hasBillingAccount) {
                            setConfirmOptions({
                              ...Helper.initialiseConfirmOptions(),
                              display: true,
                              heading: "Settle Account",
                              text: "<h4>Please note:</h4><p>After making this payment, you will still need to add a payment method for your subscription.</p><p>Return to add a card to complete your payment and continue your subscription.</p>",
                              onDiscard: () => {
                                submitPayFastCheckout(true, false, true);
                              },
                              discardButtonText: "Settle Only",
                              cancelButtonText: "Back To Change Payment Method",
                              confirmButtonText: subscriptionButtons[0].text,
                              showDiscard: true,
                              onConfirm: subscriptionButtons[0].onClick
                            });
                          }
                          else {
                            submitPayFastCheckout(true, false, true)
                          }
                        }}
                        w={"100%"}
                        disabled={submitting} >
                      Once Off Payment
                    </MantineButton>
                  </Box>

                </Box>
            }

            <Divider my="30px" />

            {paymentProblem &&
                <Box mb="xl" p="md" bg="red.0" style={{ borderRadius: 8, border: '1px solid var(--mantine-color-red-2)' }}>
                  <Text c="red.7" size="sm" fw={500}>
                    {!!paymentErrorMessage ? paymentErrorMessage :
                        "We were unable to process your payment. Please confirm your account details are correct and that sufficient funds are available, then try again."
                    }
                  </Text>
                </Box>
            }


            <Box mt="xl">
              {displayBillingDetailsFormItems(false)}
              <MantineButton disabled={submitting} onClick={() => { if (masterAdminBlocked()) return; saveBillingDetails(false); }}>
                Update Billing Details
              </MantineButton>
            </Box>
          </Box>

          <Box className={styles.sectionCard} mt="sm">
            <CancelSubscription isCancelled={isCancelled} onCancelCompleted={() => refreshBillingAccount()} />
          </Box>
        </Flex>

        <Box className={styles.sectionCard} miw={200} w={'100%'} maw={640}>
          <UsageSummary
            subscriptionInfo={subscriptionInfo as SubscriptionInfoLike}
            usageStatsQuery={usageStatsQuery}
          />

          <Divider my="30px" />

          <Flex justify="space-between" align="end">
            <Text fw={700}>Next month’s estimated bill <Text span size="12px" fw={400} c="gray.6">(Usage to date)</Text></Text>
            <Flex direction="column" align="end">
              <Flex align="baseline" gap={4}>
                <Text fw={700} size="xl">R {licenceFeeNextExclVAT.toFixed(2)}</Text>
                <Text size="12px" c="gray.6">Excl VAT</Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Flex>
    );
  };

  const getSMSRateExVAT = (): number => {
    if (!subscriptionInfo || !Array.isArray(subscriptionInfo.SMSRateExVATList)) return subscriptionInfo?.SMSRateExVAT ?? 0;

    let list = subscriptionInfo.SMSRateExVATList;

    let minCount = list.map((x: any) => x.LowCount).sort((a: any, b: any) => a - b)[0];

    let cc = creditCount ?? 0;

    if (cc < minCount) return subscriptionInfo?.SMSRateExVAT ?? 0;

    let matches = list.filter((x: any) => x.LowCount <= cc && (!x.HighCount || x.HighCount >= cc));

    return matches.length > 0 ? matches[0].RateExclVAT : (subscriptionInfo?.SMSRateExVAT ?? 0);
  };

  const displaySMS = () => {

    let canAccess = subscriptionInfo.AccessStatus === Enums.AccessStatus.Live || subscriptionInfo.AccessStatus === Enums.AccessStatus.Trial;
    let smsRateExVAT = getSMSRateExVAT();

    let smsRateInclVAT = smsRateExVAT * (1 + (subscriptionInfo.VATPercentage ?? 0) / 100);

    let billingAccount = subscriptionInfo.BillingAccount?.IsActive === false ? null : subscriptionInfo.BillingAccount;

    if (billingAccount && billingAccount.BillingProvider === (Enums.BillingProvider as any).Custom) {
      canAccess = true;
    }

    let canBuy = canAccess && smsRateExVAT > 0;

    let availableCredits = (subscriptionInfo.SMSCreditsPurchased ?? 0) - (subscriptionInfo.SMSCreditsUsed ? subscriptionInfo.SMSCreditsUsed : 0);
    let isLowBalance = availableCredits < 100;

    return (<Box className={styles.smsTabContainer} pb={60}>
      {/*
      Temporarily removed:
      <Box className={styles.smsToggleContainer}>
        <Flex align="center" gap="md">
          <Switch
            checked={smsEnabled}
            onChange={(event) => setSmsEnabled(event.currentTarget.checked)}
            size="md"
          />
          <Box>
            <Title order={4}>Enable SMS</Title>
            <Text size="sm" c="dimmed">
              Enable SMS notifications to be sent to employees and customers on job updates and other communication.
            </Text>
          </Box>
        </Flex>
      </Box>*/}

      <Box className={styles.availableSmsBox} mt={'lg'}>
        <Flex justify="space-between" align="center" w="100%">
          <Flex align="center" gap="md">
            <Text size="lg">Available SMSs</Text>
            <Text size="xl" fw={700}>{availableCredits}</Text>
          </Flex>
          {isLowBalance && (
            <Flex align="center" gap="xs" className={styles.lowBalanceWarning}>
              <IconBulb size={20} />
              <Text>SMS balance is low, please top up to avoid any sending errors.</Text>
            </Flex>
          )}
        </Flex>
      </Box>

      {canBuy ? (
        <Grid gutter={40}>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Box mb="xl">
              <Title order={4} mb="sm">Top up credits</Title>
              <ScNumberControl
                name="creditCount"
                error={creditError as any}
                value={creditCount}
                required={true}
                label="Number of credits"
                placeholder="e.g. 1000"
                onChange={(val: any) => {
                  let num = val === '' || val === undefined || val === null ? undefined : parseInt(val.toString());
                  if (num !== undefined && isNaN(num)) num = undefined;
                  setCreditCount(num);
                  setCreditError("");
                }}
                decimalScale={0}
                min={minCredits}
                mt="sm"
                hideControls
                style={{ maxWidth: 400 }}
                onKeyDown={(e: any) => {
                  if (e.key === "e" || e.key === "E" || e.key === ".") {
                    e.preventDefault();
                  }
                }}
              />
            </Box>

            <Box className={styles.orderSummaryCard} p={'md'}>
              <Title order={4} mb="md">Order summary</Title>

              <Flex justify="space-between" mb="xs">
                <Text>Credits</Text>
                <Text fw={500}>{creditCount ?? 0}</Text>
              </Flex>

              <Flex justify="space-between" mb="xs">
                <Text>Rate (excl VAT)</Text>
                <Text fw={500}>R {smsRateExVAT.toFixed(2)}</Text>
              </Flex>

              <Flex justify="space-between" mb="xs">
                <Text>Subtotal</Text>
                <Text fw={500}>R {(smsRateExVAT * (creditCount ?? 0)).toFixed(2)}</Text>
              </Flex>

              <Flex justify="space-between" mb="xs">
                <Text>VAT ({subscriptionInfo.VATPercentage}%)</Text>
                <Text fw={500}>R {(smsRateExVAT * (subscriptionInfo.VATPercentage ?? 0) / 100 * (creditCount ?? 0)).toFixed(2)}</Text>
              </Flex>

              <Flex justify="space-between" align="center" mb="xl">
                <Text fw={700} size="lg">Total</Text>
                <Text fw={700} size="lg">R {(smsRateInclVAT * (creditCount ?? 0)).toFixed(2)}</Text>
              </Flex>

              <Divider my="md" />

              <Flex justify="center">
                <MantineButton onClick={buyCreditsPrompt} >
                  Buy SMS credits
                </MantineButton>
              </Flex>
            </Box>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Title order={4} mb="sm">Pricing tiers</Title>
            <Box className={styles.pricingTableContainer}>
              <Table verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Credit range</Table.Th>
                    <Table.Th>Rate (excl VAT)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {subscriptionInfo?.SMSRateExVATList?.map((rate: any, idx: any) => (
                    <Table.Tr key={idx}>
                      <Table.Td>{rate.LowCount}{rate.HighCount ? ` - ${rate.HighCount}` : "+"}</Table.Td>
                      <Table.Td>R {rate.RateExclVAT.toFixed(2)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          </Grid.Col>
        </Grid>
      ) : !canAccess ? (
        <Box className={styles.paymentAlert} mt="xl">
          <Title order={3}>You do not have access to purchase SMS credits at this point in time.</Title>
          <Text component="p">Please make sure your account is in good standing.</Text>
        </Box>
      ) : (
        <Title order={3} mt="xl">Please contact support to resolve SMS credit price</Title>
      )}

      {messageCredits ? (
        <Box mt="xl">
          <Title order={4} className={styles.purchaseHistoryTitle}>Purchase history</Title>
          {messageCredits.length > 0 ? (
            <Box className={styles.pricingTableContainer}>
              <SimpleTable
                data={messageCredits.sort((a: any, b: any) => (a.PurchaseDate > b.PurchaseDate ? -1 : 1))}
                mapping={[
                  {
                    key: 'PurchaseDate',
                    label: 'Date',
                    valueFunction: (item: MessageCredit) => Time.formatDate(item.PurchaseDate),
                  },
                  {
                    key: 'Quantity',
                    label: 'Quantity',
                  },
                  {
                    key: 'Reference',
                    label: 'Reference',
                  },
                  {
                    key: 'MessageType',
                    label: 'Type',
                    valueFunction: (item: MessageCredit) => Enums.getEnumStringValue(Enums.MessageType, item.MessageType),
                  },
                ]}
                stylingProps={{
                  rowBorders: true,
                  compact: true,
                }}
              />
            </Box>
          ) : (
            <Text c="dimmed">No purchase history found.</Text>
          )}
        </Box>
      ) : ""}

    </Box>);
  }

  const displayBillingHistory = () => {

    return (<Box className={styles.billingHistoryContainer}>

        {/* Hint Box - only show if user doesn't have a VAT number */}
        {noVATNumber && (
          <Box
            bg="scBlue.0"
            bd="1px solid scBlue.2"
            p="1rem 1.25rem"
            mb="1.5rem"
            maw="350px"
            style={{ borderRadius: 'var(--mantine-radius-sm)' }}
          >
            <Flex align="flex-start" gap="sm">
              <IconBulb size={18} color="#001aff" style={{ flexShrink: 0, marginTop: 2 }} />
              <Box>
                <Text fw={700} c="scBlue.7" size="sm" mb={4} >Hint</Text>
                <Text size="sm" lh={1.4}>
                  Update your <strong>VAT number</strong> under the Subscription tab to include them in invoices.
                </Text>
              </Box>
            </Flex>
          </Box>
        )}

        {/* Invoice Table Header */}

        {/* Invoice Table */}
        <Box
            bd="1px solid #00000055"
            style={{ borderRadius: 'var(--mantine-radius-sm)', overflow: 'hidden' }}>
          <SimpleTable
            data={invoices}
            firstColumnOffset
            mapping={[
              {
                key: 'InvoiceNumber',
                label: 'Invoice number',
                valueFunction: (item: Invoice) => (
                  <Text
                    c="scBlue"
                    fw={700}
                    style={{ cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => previewInvoice(item.ID)}
                    title="Click to preview invoice"
                  >
                    {item.InvoiceNumber || '-'}
                  </Text>
                ),
                columnWidth: '20%'
              },
              {
                key: 'TotalInclusive',
                label: 'Amount',
                valueFunction: (item: Invoice) => (
                  <Text fw={800} size="sm">R {(item.TotalInclusive || 0).toFixed(2)}</Text>
                ),
                columnWidth: '15%'
              },
              {
                key: 'InvoiceDate',
                label: 'Date',
                valueFunction: (item: Invoice) => moment(item.InvoiceDate).format('DD MMM YYYY'),
                columnWidth: '10%'
              },
              {
                key: 'Status',
                label: 'Status',
                valueFunction: (item: Invoice) => {
                  const isPaid = item.AmountDue == 0;
                  const isPartiallyPaid = (item.TotalInclusive - item.AmountDue) > 0;

                  return (
                      <Badge
                        color={isPaid ? 'green' : isPartiallyPaid ? 'yellow' : 'red'}
                        variant="light"
                        size="sm"
                      >
                        {isPaid ? 'Paid' : isPartiallyPaid ? 'Partially Paid' : 'Unpaid'}
                      </Badge>
                  );
                },
                columnWidth: '23%',
                alignCenter: true
              },
              {
                key: 'CustomReference',
                label: 'Custom reference',
                valueFunction: (item: Invoice) => (
                  <Flex gap="xs" align="center">
                    <input
                      type="text"
                      className={styles.referenceInput}
                      value={editedReferences[item.ID] ?? item.CustomReference ?? ''}
                      onChange={(e: any) => handleReferenceChange(item.ID, e.target.value)}
                      placeholder="Enter reference..."
                      maxLength={100}
                    />
                    {hasReferenceChanged(item) && (
                      <MantineButton
                        size="xs"
                        variant="filled"
                        color="scBlue"
                        onClick={() => saveCustomReference(item.ID)}
                        loading={savingReference === item.ID}
                      >
                        Save
                      </MantineButton>
                    )}
                  </Flex>
                ),
                columnWidth: '32%'
              }
            ]}
            controls={[]}
            stylingProps={{
              compact: true,
              rows: true,
              darkerText: true
            }}
            tableActionStates={{}}
            height="auto"
          />

          {invoices.length === 0 && (
            <Box p="xl" ta="center">
              <Text c="dimmed">No invoices found</Text>
            </Box>
          )}
        </Box>

        {/* Pagination with Refresh */}
        <Flex mt="md" align="center" gap="sm">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => getInvoices(invoicePageIndex)}
            title="Refresh list"
          >
            <IconRefresh size={18} />
          </ActionIcon>
          <Box style={{ flex: 1 }}>
            <ScPagination
              totalElements={invoiceTotalResults}
              totalOnPage={invoices.length}
              currentPage={invoicePageIndex}
              pageSize={invoicePageSize}
              onChange={(newPageState: any) => getInvoices(newPageState.pageIndex)}
              hidePageSizeDropdown
            />
          </Box>
        </Flex>
      </Box>);
  };

  const tabData = useMemo(() => {

    let tabOptions: any = {};

    tabOptions.subscription = {
      label: 'Subscription',
      access: true
    };

    if (!thirdPartyBilling) {
      tabOptions.billingHistory = {
        label: 'Billing History',
        access: true
      };
    }

    if (thirdPartyBilling || !canCreateBilling) {
      tabOptions.contact = {
        label: 'Contacts',
        access: true
      };
    }

    tabOptions.sms = {
      label: 'SMS Credits',
      access: true
    };

    return tabOptions;
  }, [thirdPartyBilling, canCreateBilling])

  return (
    <Box className={styles.settingsContainer}>
      {/* <Flex className={styles.row}>
        <Box className={styles.title}>
          <Breadcrumbs
            crumbs={[
              { text: 'Settings', link: '', suppressLink: true },
              { text: 'Subscriptions', link: '/settings/subscription/manage' },
            ]}
          />
        </Box>
      </Flex> */}
      <Flex w={'100%'} p={'lg'}>
        <SettingsMenu urlPath={currPath} />
        <Box w={'100%'}>

          <MantineTabs color={'scBlue'} value={selectedTab} onChange={(value: any) => value && setSelectedTab(value)}
            classNames={{
              tab: tabStyles.scTab,
              list: tabStyles.scTabList,
              tabLabel: tabStyles.scTabLabel
            }}
          >
            <MantineTabs.List mb={0} pb={0}>
              {
                Object.entries(tabData).filter(([, x]: any) => true).map(
                  ([tabName, d]: any) => d && <MantineTabs.Tab value={tabName}
                    disabled={!d.access}
                    key={tabName + 'tab'}>{d.label}</MantineTabs.Tab>
                )
              }
            </MantineTabs.List>
          </MantineTabs>

          {/*
          <Tabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabs={[
              { text: 'Subscription' },
              { text: 'SMS', suppressCount: true }
            ]} /> */}

          {!initialising && <>

            {(() => {
              switch (selectedTab) {
                case "subscription":
                  return thirdPartyBilling ? displayThirdPartyBilling() : (canCreateBilling ? displayCreateBilling() : displayManageBilling())
                case "billingHistory":
                  return displayBillingHistory()
                case "sms":
                  return displaySMS()
                case "contact":
                  return billingDetails ? <BillingContactTable billingDetails={billingDetails} setBillingDetails={setBillingDetailsFromContactTable} isThirdParty={thirdPartyBilling} /> : null
              }
            })()}

          </>}

        </Box>
      </Flex>

      <ConfirmAction
        options={confirmOptions}
        setOptions={setConfirmOptions}
      />

    </Box>
  );
}

export default SubscriptionManage;

export const Route = createFileRoute('/_authenticated/settings/subscription/manage')({ component: SubscriptionManage })
